<template>
  <div class="upload-file">
    <ul class="list-files" v-if="hasFiles">
      <li v-for="(file, index) in displayFiles" :key="index">
        <div class="col-file">
          <b-icon icon="file-earmark" aria-hidden="true"></b-icon>
        </div>
        <div class="col-file upload-info">
          <span class="about-progress">
            <b>{{ file.name }}</b>
            <i v-if="progressUpload != null">{{
              progressUpload >= 100 ? "Pronto" : "Carregando..."
            }}</i>
          </span>
          <span v-if="progressUpload != null" class="progress-bar"></span>
        </div>
        <div class="col-file">
          <b-icon icon="x-circle-fill" aria-hidden="true" @click="removeFile(index)"></b-icon>
        </div>
      </li>
    </ul>
    <div class="wrap-input-file" @dragover.prevent="onDragover" @drop.prevent="onDrop">
      <input ref="fileInput" type="file" class="upload-files" :multiple="allowMultiple"
        :accept="accept || (!noUploadPreview ? 'image/png, image/jpg, image/jpeg, .pdf' : '.xls, .csv')"
        @change="addFile($event)" />
      <div class="description-input-file" v-if="!noUploadPreview">
        <b-icon icon="upload" aria-hidden="true"></b-icon>
        <h5>Arraste {{ allowMultiple ? 'os arquivos' : 'um arquivo' }} ou</h5>
        <button type="button">Selecione</button>
        <span>{{ hint || 'Apenas arquivos PNG, JPG, JPEG, PDF com tamanho máximo de 5MB.' }}</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "UploadMultipleFiles",
  props: {
    fileName: String,
    noUploadPreview: {
      type: Boolean,
      default: false
    },
    allowMultiple: {
      type: Boolean,
      default: false
    },
    accept: {
      type: String,
      default: null
    },
    hint: {
      type: String,
      default: null
    },
    value: {
      type: [Array, File, null],
      default: null
    }
  },
  data() {
    return {
      currentFiles: this.value || (this.allowMultiple ? [] : null),
      progressUpload: null,
    };
  },
  watch: {
    value: {
      handler(newVal) {
        if (!newVal) {
          this.currentFiles = this.allowMultiple ? [] : null;
          this.progressUpload = null;
        } else {
          this.currentFiles = newVal;
        }
      },
      immediate: true
    }
  },
  computed: {
    hasFiles() {
      if (this.allowMultiple) {
        return this.currentFiles && this.currentFiles.length > 0;
      }
      return this.currentFiles || this.fileName;
    },
    displayFiles() {
      if (this.allowMultiple) {
        return this.currentFiles || [];
      }
      if (this.fileName) {
        return [{ name: this.fileName }];
      }
      return this.currentFiles ? [this.currentFiles] : [];
    }
  },
  methods: {
    onDragover(event) {
      event.preventDefault();
      const element = event.currentTarget;
      element.classList.add("on-drag-over");
    },
    onDrop(event) {
      event.preventDefault();
      const element = event.currentTarget;
      element.classList.remove("on-drag-over");

      const files = event.dataTransfer.files;
      if (files.length > 0) {
        this.handleFiles(files);
      }
    },
    progressTimer() {
      if (this.progressUpload !== null) return;

      this.progressUpload = 0;
      const timerProgress = setInterval(() => {
        this.progressUpload += 1;
        if (this.progressUpload >= 100) {
          clearInterval(timerProgress);
        }
      }, 10);
    },
    addFile(event) {
      const files = event.target.files;
      if (files.length > 0) {
        this.handleFiles(files);
      }
    },
    handleFiles(files) {
      const maxSize = 5 * 1024 * 1024;
      const validFiles = Array.from(files).filter(file => {
        if (file.size > maxSize) {
          this.$toast && this.$toast.error(`Arquivo ${file.name} é muito grande. Tamanho máximo: 5MB`);
          return false;
        }
        return true;
      });

      if (validFiles.length === 0) return;

      if (this.allowMultiple) {
        this.currentFiles = [...(this.currentFiles || []), ...validFiles];
      } else {
        this.currentFiles = validFiles[0];
      }

      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }

      this.$emit("input", this.currentFiles);
      this.progressTimer();
    },
    removeFile(index) {
      if (this.allowMultiple) {
        this.currentFiles = this.currentFiles.filter((_, i) => i !== index);
        if (this.currentFiles.length === 0) {
          this.progressUpload = null;
        }
      } else {
        this.currentFiles = null;
        this.progressUpload = null;
      }
      this.$emit("input", this.currentFiles);
    },
    clearFiles() {
      this.currentFiles = this.allowMultiple ? [] : null;
      this.progressUpload = null;
      this.$emit("input", this.currentFiles);
    }
  },
};
</script>

<style lang="scss">
.list-files {
  width: 100%;
  display: flex;
  flex-direction: column;
  row-gap: 10px;
  margin: 0 0 24px;
  padding: 0;

  li {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 20px;
    row-gap: 20px;
    list-style: none;
    box-sizing: border-box;
  }

  .col-file {
    flex: 0 0 32px;
  }

  svg {
    font-size: 24px;

    &.bi-x-circle-fill {
      color: #b3b3b3;
      cursor: pointer;
      transition: color 0.3s ease;

      &:hover {
        color: #999;
      }
    }
  }

  .upload-info {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-direction: column;

    .about-progress {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 3px;

      b {
        text-align: left;
        flex: 1;
        max-width: 80%;
        font-weight: 400;
        font-size: 14px;
        line-height: 16px;
        color: #000000;
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
      }

      i {
        flex: 0 0 42px;
        font-style: normal;
        font-weight: 400;
        font-size: 14px;
        line-height: 16px;
        color: #666666;
      }
    }

    .progress-bar {
      background: #dbdbdb;
      width: 100%;
      height: 6px;
      position: relative;
      border-radius: 50px;
      overflow: hidden;

      &::before {
        content: "";
        background: $gradient1;
        width: 0%;
        height: 6px;
        animation-name: progressBar;
        animation-duration: 1.7s;
        animation-fill-mode: forwards;
        position: absolute;
        top: 0;
        left: 0;
      }

      @keyframes progressBar {
        0% {
          width: 0;
        }

        100% {
          width: 100%;
        }
      }
    }
  }
}

.wrap-input-file {
  width: 100%;
  border: 1px dashed #b3b3b3;
  border-radius: 5px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  row-gap: 20px;
  padding: 20px;
  box-sizing: border-box;
  position: relative;
  transition: border-color 0.3s ease;
  &.on-drag-over {
    border-color: $purple;
    background-color: rgba(156, 39, 176, 0.1);
  }

  input {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
    opacity: 0;
    cursor: pointer;
  }

  .description-input-file {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    row-gap: 20px;
    position: relative;
    z-index: 0;
    pointer-events: none;

    svg {
      font-size: 24px;
      color: #666;
    }

    h5 {
      font-weight: 400;
      font-size: 20px;
      line-height: 24px;
      color: #000000;
      margin-bottom: 0;
    }

    button {
      font-weight: 700;
      font-size: 20px;
      line-height: 24px;
      color: #ffffff;
      background: $purple;
      width: 153px;
      height: 40px;
      border: 0;
      border-radius: 5px;
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;
      pointer-events: none;
    }

    span {
      font-weight: 400;
      font-size: 14px;
      line-height: 16px;
      color: #666666;
      text-align: center;
    }
  }
}

.loading-file {
  min-height: 250px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  .spinner-border {
    width: 32px;
    height: 32px;
    margin-bottom: 1rem;
  }

  span {
    font-weight: 700;
    font-size: 20px;
    line-height: 32px;
    color: $purple;
  }
}
</style>
